import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const STORAGE_KEY = 'announcementModalDismissedAt';
const DORMANT_MS = 60 * 60 * 1000; // 1 hour

// `pathname` is supplied by AppLayout. gatsby-plugin-layout keeps the layout
// (and this Modal) mounted across client-side navigations, so a `[]` effect
// would only run on hard refresh. Re-running when pathname changes lets a
// fresh route trigger the storage check — that's how "open on first visit to
// any page" works after the user clears localStorage or after the dormancy
// timer would otherwise have to wait for a remount.
const Modal = ({ pathname }) => {
  // Start closed so SSR markup matches; decide on the client in useEffect.
  const [isOpen, setIsOpen] = useState(false);
  const reopenTimerRef = useRef(null);

  const scheduleReopen = (delayMs) => {
    if (reopenTimerRef.current) {
      clearTimeout(reopenTimerRef.current);
    }
    reopenTimerRef.current = setTimeout(() => {
      setIsOpen(true);
    }, Math.max(0, delayMs));
  };

  useEffect(() => {
    let dismissedAt = 0;
    try {
      dismissedAt = Number(window.localStorage.getItem(STORAGE_KEY)) || 0;
    } catch (e) {
      setIsOpen(true);
      return;
    }
    const elapsed = Date.now() - dismissedAt;
    if (elapsed >= DORMANT_MS) {
      setIsOpen(true);
    } else {
      scheduleReopen(DORMANT_MS - elapsed);
    }
    return () => {
      if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
    };
  }, [pathname]);

  const handleClose = () => {
    setIsOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {
      // localStorage unavailable (private mode, quota, etc.) — modal will
      // re-show next navigation, which is acceptable.
    }
    scheduleReopen(DORMANT_MS);
  };

  return (
    <Transition
      show={isOpen}
      as={Fragment}
    >
      <Dialog
        onClose={handleClose}
        className="relative z-50"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="relative">

              <div className="absolute -top-[.5rem] -right-[.5rem] sm:top-[1.75rem] sm:-right-[.5rem] z-20">
                <button
                  type="button"
                  className="p-3 rounded-full bg-gray-600 text-white shadow-lg focus-visible:outline-none"
                  onClick={handleClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <Dialog.Panel className="relative bg-primary-50 border-4 lg:border-8 border-primary-500 rounded-5xl md:rounded-9xl p-6 pb-8 lg:py-10 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-xl sm:w-full sm:min-h-[30rem]">
                <div>
                  <div className="mt-3 sm:mt-0 sm:ml-4">
                    <Dialog.Title as="h3" className="uppercase font-normal text-md">
                      Announcement 📣
                    </Dialog.Title>
                    <h4 className="mt-2 text-2xl font-bold leading-tight">
                      Our South Side Location is Moving!
                    </h4>
                    <div className="mt-4">
                      <p>
                        Effective <strong>June 1, 2026</strong> our south side location is merging with our head office at the following location:
                      </p>
                      <p className="mt-4">
                        10633 109 Street<br />
                        Mon-Fri, 8:00 a.m. to 4:30 p.m.
                      </p>
                      <p className="mt-4">
                        <strong>Contact phone number and email remain the same:</strong><br />
                        (780) 437-5533<br />
                        <a href="mailto:brenda@royalstamp.ca">brenda@royalstamp.ca</a>
                      </p>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default Modal;
